<template>
  <div class="profile">
    <v-card class="text-center">
      <v-card-text class="py-0">

        <profile-image
          v-if="!hideProfileImage"
          :user-status="userStatus"
          :hide-user-status="hideUserStatus"
          size="120"
        />

        <div class="profile-info">
          <router-link to="/account" class="profile-link">
            <h3>{{ name }}</h3>
            <h4>
              {{ $tc(
                'profile.currentPoints',
                points,
                { points: points.toLocaleString() }
              ) }}
            </h4>
          </router-link>

          <template v-if="!hideAdditionalInfo">
            <ul>
              <li v-if="showEmail"><span class="font-weight-bold">{{ $t('registerScreen.placeholderEmail') }}:</span> {{ user.email }}</li>
              <li v-if="phoneNumber"><span class="font-weight-bold">{{ $t('registerScreen.placeholderPhonenumber')}}:</span> {{ phoneNumber }}</li>
              <li v-if="showCustomerId"><span class="font-weight-bold">{{$t('registerScreen.placeholderBirthday')}}:</span> {{ user.customer_id }}</li>
              <li v-if="showStreet">
                <span class="font-weight-bold">{{$t('registerScreen.placeholderStreet')}}:</span>
                {{ user.address.street }}
                <span v-if="showStreetNumber">{{ user.address.street_number }}</span>
              </li>
              <li v-if="showCityCode"><span class="font-weight-bold">{{$t('registerScreen.placeholderCityCode')}}:</span> {{ user.address.city_code }}</li>
              <li v-if="showCity"><span class="font-weight-bold">{{$t('registerScreen.placeholderCity')}}:</span> {{ user.address.city }}</li>
              <li v-for="field in customFields" :key="field.key">
                <span class="font-weight-bold pr-1">{{field.name}}:</span>
                <span v-if="field.icon"><v-icon small :color="field.icon.color">{{ field.icon.value }}</v-icon></span>
                <span v-else>{{ field.value }}</span>
              </li>
            </ul>
          </template>
        </div>

      </v-card-text>
    </v-card>
  </div>
</template>

<script>
  import _ from 'lodash'
  import moment from 'moment'
  import {mapGetters} from 'vuex'
  import ProfileImage from "@/components/profile/ProfileImage"
  import {
    mdiQrcode,
    mdiCamera,
    mdiCheck,
    mdiCancel
  } from '@mdi/js'

  export default {
    name: "Profile",
    components: {
      ProfileImage
    },
    props: {
      data: {
        type: Object,
        default: () => {},
      }
    },
    data() {
      return {
        customFields: [],
        icons: {
          mdiQrcode,
          mdiCamera,
          mdiCheck,
          mdiCancel
        }
      }
    },
    computed: {
      ...mapGetters([
        'user',
        'authSettings',
        'customUserFields',
        'customerRelationship'
      ]),
      name() {
        return _.get(this.user, 'name');
      },
      points() {
        return _.get(this.customerRelationship, 'points');
      },
      userStatus() {
        const statusArray =
          _.get(this.customerRelationship, 'status', []);

        if (!_.isEmpty(statusArray)) {
          return statusArray.filter((status) => {
            return !_.isNil(status) &&
              !_.isEmpty(_.get(status, 'icon'));
          })
        }

        return [];
      },
      hideAdditionalInfo() {
        return _.get(this.data, 'hide_additional_info', true)
      },
      hideProfileImage() {
        return _.get(this.data, 'hide_profile_image', false)
      },
      hideUserStatus() {
        return _.get(this.data, 'hide_user_status', false)
      },
      showEmail() {
        return _.get(this.user, 'email') && _.get(this.authSettings, ['attributes', 'email', 'show_in_app'], true);
      },
      phoneNumber() {
        return _.get(this.user, 'phone_number', false);
      },
      showBirthday() {
        return _.get(this.user, 'birthday') && _.get(this.authSettings, ['attributes', 'birthday', 'show_in_app'], true);
      },
      showCustomerId() {
        return _.get(this.user, 'customer_id') && _.get(this.authSettings, ['attributes', 'customer_id', 'show_in_app'], true);
      },
      showStreet() {
        return _.get(this.user, 'address.street') && _.get(this.authSettings, ['attributes', 'street', 'show_in_app'], true);
      },
      showStreetNumber() {
        return _.get(this.user, 'address.street_number') && _.get(this.authSettings, ['attributes', 'street_number', 'show_in_app'], true);
      },
      showCityCode() {
        return _.get(this.user, 'address.city_code') && _.get(this.authSettings, ['attributes', 'city_code', 'show_in_app'], true);
      },
      showCity() {
        return _.get(this.user, 'address.city') && _.get(this.authSettings, ['attributes', 'city', 'show_in_app'], true);
      }
    },
    methods: {
      getCustomUserFields() {
        if (!this.customUserFields) {
          return null;
        }

        const content = [];
        const userFields = _.get(this.user, 'custom_fields', []);

        for (const field of this.customUserFields) {
          // get the value for the custom user field from the user object
          const userFieldValue = userFields.filter((f) => f.key === field.key)[0];
          let value = _.get(userFieldValue, 'value', _.get(field, 'metadata.default'));

          if (field.type === 'date') {
            if (!_.isEmpty(value)) { // if value is present
              const customDate = moment(value, 'YYYY-MM-DD'); // try to parse date

              if (moment.isMoment(customDate) && customDate.isValid()) { // if customDate is a valid date
                value = customDate.format('DD.MM.YYYY'); // format for showing on screen
              }
            }
          }

          if (field.type === 'location') {
            value = _.get(value, 'name', '');
          }

          if (field.type === 'select') {
            let choice = {};

            if (value !== undefined) {
              choice = field.metadata.choices.filter((c) => c.value === value)[0]; // get the user's selected choice
            } else {
              choice = field.metadata.choices.filter((c) => _.has(c, 'default'))[0]; // get the default value
            }

            if (!_.isEmpty(choice)) {
              value = choice.title;
            }
          }

          if (!!_.get(field, 'show_in_app', false) && (!_.isEmpty(value) || _.isBoolean(value)) && value !== '') {
            let userFieldValue = '';
            let icon = false;

            if (_.isBoolean(value)) {
              icon = {value: this.icons.mdiCancel, color: 'red'};
              if (value) {
                icon = {value: this.icons.mdiCheck, color: 'green'};
              }
            } else {
              userFieldValue = `${value}`;
            }

            content.push({
              key: field.key,
              name: field.name,
              value: userFieldValue,
              icon
            });
          }
        }
        this.customFields = content;
      }
    },
    mounted() {
      this.getCustomUserFields();
    }
  }
</script>

<style scoped lang="scss">
  .profile {
    margin-bottom: 42px;
  }
  .profile-info {
    display: flex;
    justify-content: center;
    flex-direction: column;
    h3 {
      line-height: normal;
      text-transform: none;
      font-size: 1.5rem;
      font-weight: 400;
    }
    h4 {
      font-weight: 900;
    }
    ul {
      text-align: left;
      list-style: none;
      margin: 1em auto 0;
      padding: 0;
      display: flex;
      justify-content: flex-start;
      flex-direction: column;
    }
  }
  .profile-link {
    color: var(--text);
  }
  .v-sheet.v-card:not(.v-sheet--outlined) {
    box-shadow: none;
  }
</style>
